// cleanup_convert_to_typescript_file_SMBPYMT
// cleanup_remove_external_libraries_SMBPYMT

import { kebabCase } from 'lodash';
import { curry, compose, includes, defaultTo, prop } from 'lodash/fp';

import { TrashCanNextIcon, UseFundsNextIcon } from '@glu/icons-react';
import { MultiSelectFilter } from '@glu/grid-react';

import formatters, {
  getValueOrDefault,
  formatCurrency
} from 'components/basicGrid/helpers/formatters';
import { PAYMENTS_COLUMNS as columns } from 'components/widgets/payments/web/payments.constants';
import {
  ACTIONS,
  ACTIONS_COLUMN,
  AG_GRID_COLUMN_FILTER_TYPES,
  COLUMN_TYPES,
  CURRENCY,
  PAYMENT_STATUS,
  PAYMENT_TYPES,
  SIMPLIFIED_PAYMENT_TYPES,
  WORKFLOWS
} from 'constants.js';

const { DATE, NUMBER } = AG_GRID_COLUMN_FILTER_TYPES;
const { APPROVE, COPY, COPY_AS_PAYMENT, DELETE, MODIFY, REJECT, VIEW } = ACTIONS;
const { REQUIRES_OTHERS_APPROVAL } = PAYMENT_STATUS;
const { EMPLOYEE, LOAN_DRAW, LOAN_PAYMENT, TAX_PAYMENT } = SIMPLIFIED_PAYMENT_TYPES;

const CurrencyDisplay = (field) => {
  return ({ rowData }) => formatCurrency(rowData[field]);
};

const valueGetter = (locale, isNumber) =>
  getValueOrDefault(locale, locale['common.grid.empty'], isNumber);

const getMultiSelectOptions = (records, columnName) => {
  const allValues = records.map((record) => record[columnName]);
  const uniqueValues = [...new Set(allValues)].sort();
  return uniqueValues.map((label) => ({ id: label, name: label, value: label }));
};

const getViewRoute = (paymentType) => {
  switch (paymentType) {
    case PAYMENT_TYPES.WIRE.INTERNATIONAL_WIRE:
    case PAYMENT_TYPES.WIRE.EXPEDITED:
    case PAYMENT_TYPES.ACH.REGULAR:
      return WORKFLOWS.SEND_PAYMENT;
    case PAYMENT_TYPES.COLLECT.COLLECT:
      return WORKFLOWS.COLLECT_PAYMENT;
    case PAYMENT_TYPES.LOAN.LOAN_PAYMENT:
    case PAYMENT_TYPES.LOAN.LOAN_DRAW:
      return WORKFLOWS.LOAN_PAYMENT;
    default:
      return WORKFLOWS.SEND_PAYMENT;
  }
};

const getActionRoute = (paymentType, action) => {
  switch (paymentType) {
    case PAYMENT_TYPES.WIRE.INTERNATIONAL_WIRE:
    case PAYMENT_TYPES.WIRE.EXPEDITED:
    case PAYMENT_TYPES.ACH.REGULAR:
      return `${WORKFLOWS.SEND_PAYMENT}/${action}`;
    case PAYMENT_TYPES.COLLECT.COLLECT:
      return `${WORKFLOWS.COLLECT_PAYMENT}/${action}`;
    case PAYMENT_TYPES.LOAN.LOAN_PAYMENT:
      return `${WORKFLOWS.LOAN_PAYMENT}/send`;
    case PAYMENT_TYPES.LOAN.LOAN_DRAW:
      return `${WORKFLOWS.LOAN_PAYMENT}/draw`;
    default:
      return `${WORKFLOWS.SEND_PAYMENT}/${action}`;
  }
};

const hasAction = curry((action, data) =>
  compose(includes(action), defaultTo([]), prop('data.actions'))(data)
);

/* istanbul ignore file */
const smbPaymentColumns = ({
  eventHandler,
  eventTemplate,
  history,
  locale,
  records = [],
  reload
}) => [
  {
    checkboxSelection: true,
    field: columns.id,
    filterable: false,
    showHideDisallowed: true
  },
  {
    cellRenderer: 'actions',
    field: ACTIONS_COLUMN,
    filterable: false,
    headerName: locale['common.actions'],
    resizable: false,
    sortable: false,
    showHideDisallowed: true,
    actions: [
      {
        label: locale['common.view'],
        condition: hasAction(VIEW),
        handler: ({ data }) => {
          const { bankTrace, custTrace, paymentType } = data;
          const type = kebabCase(paymentType);

          if ([LOAN_DRAW, LOAN_PAYMENT].includes(type)) {
            const loanType = type.replace('loan-', '');

            return history.push({
              state: { previous: history.location.pathname },
              pathname: `${WORKFLOWS.VIEW_SB_LOAN_PAYMENT}/${loanType}/${data.bankTrace}`
            });
          }

          if (type === EMPLOYEE) {
            return history.push({
              pathname: `${WORKFLOWS.VIEW_EMPLOYEE_PAYMENT}/${custTrace}`,
              state: { previous: history.location.pathname }
            });
          }

          if (type === TAX_PAYMENT) {
            return history.push({
              pathname: `${WORKFLOWS.VIEW_ACH_PAYMENT}/${custTrace}`,
              state: {
                previous: history.location.pathname,
                workflowStartPath: history.location.pathname,
                actionTitle: locale['smallBusinessPayments.viewTaxPayment'],
                isSmallBusiness: true
              }
            });
          }

          const pathname = `${getViewRoute(paymentType)}/${type}/${custTrace || bankTrace}`;
          return history.push({
            pathname,
            state: { previous: history.location.pathname }
          });
        }
      },
      {
        label: locale['common.copy'],
        condition: hasAction(COPY),
        handler: /* istanbul ignore next */ ({ data }) => {
          const { bankTrace, custTrace, paymentType } = data;
          const type = kebabCase(paymentType);
          if (type === EMPLOYEE) {
            return history.push({
              pathname: `${WORKFLOWS.MODIFY_EMPLOYEE_PAYMENT}/${custTrace}`,
              state: { previous: history.location.pathname }
            });
          }
          const pathname = `${getActionRoute(paymentType, COPY.toLowerCase())}/${type}/${
            custTrace || bankTrace
          }`;
          return history.push({
            pathname,
            state: { previous: history.location.pathname }
          });
        }
      },
      {
        label: locale['common.copyAsNewPayment'],
        condition: hasAction(COPY_AS_PAYMENT.replace(/\s+/g, '')),
        handler: /* istanbul ignore next */ ({ data }) => {
          const { bankTrace, custTrace, paymentType } = data;
          const type = kebabCase(paymentType);
          if (type === EMPLOYEE) {
            return history.push({
              pathname: `${WORKFLOWS.COPY_EMPLOYEE_PAYMENT}/${custTrace}`,
              state: { previous: history.location.pathname }
            });
          }

          if ([LOAN_DRAW, LOAN_PAYMENT].includes(type)) {
            const loanType = type.replace('loan-', '');

            return history.push({
              pathname: `${WORKFLOWS.SB_LOAN_PAYMENT}/copy/${loanType}/${data.bankTrace}`
            });
          }

          const pathname = `${getActionRoute(paymentType, COPY.toLowerCase())}/${type}/${
            custTrace || bankTrace
          }`;
          return history.push({
            pathname,
            state: { previous: history.location.pathname }
          });
        }
      },
      {
        condition: hasAction(MODIFY),
        label: locale['common.modify'],
        handler: /* istanbul ignore next */ ({ data }) => {
          const { bankTrace, custTrace, paymentType } = data;
          const type = kebabCase(paymentType);
          if (type === EMPLOYEE) {
            return history.push({
              pathname: `${WORKFLOWS.MODIFY_EMPLOYEE_PAYMENT}/${custTrace}`,
              state: { previous: history.location.pathname }
            });
          }
          if (type === TAX_PAYMENT) {
            return history.push({
              pathname: `${WORKFLOWS.MODIFY_ACH_PAYMENT}/${custTrace}`,
              state: {
                previous: history.location.pathname,
                workflowStartPath: history.location.pathname,
                actionTitle: locale['smallBusinessPayments.modifyTaxPayment'],
                isSmallBusiness: true
              }
            });
          }
          const pathname = `${getActionRoute(paymentType, MODIFY.toLowerCase())}/${type}/${
            custTrace || bankTrace
          }`;
          return history.push({
            pathname,
            state: { previous: history.location.pathname }
          });
        }
      },
      {
        label: locale['common.delete'],
        condition: hasAction(DELETE),
        handler: /* istanbul ignore next */ (data) => {
          eventHandler([data], DELETE.toLowerCase(), reload);
        },
        templateProps: { icon: TrashCanNextIcon, iconTitle: locale['common.delete'] },
        template: (data) => eventTemplate([data], DELETE.toLowerCase())
      },
      {
        label: locale['common.approve'],
        condition: hasAction(APPROVE),
        handler: /* istanbul ignore next */ (data) => {
          eventHandler([data], APPROVE.toLowerCase(), reload);
        },
        templateProps: { icon: UseFundsNextIcon, iconTitle: locale['common.approve'] },
        template: (data) => eventTemplate([data], APPROVE.toLowerCase())
      },
      {
        label: locale['common.reject'],
        condition: hasAction(REJECT),
        handler: /* istanbul ignore next */ (data) => {
          eventHandler([data], REJECT.toLowerCase(), reload);
        },
        template: (data) => eventTemplate([data], REJECT.toLowerCase())
      }
    ]
  },
  {
    headerName: locale['common.amount'],
    field: columns.amount,
    comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
    valueFormatter: formatters.toDecimal({ locale, name: columns.amount }),
    filterValueGetter: ({ data }) => Number(data.amount),
    filter: NUMBER,
    type: COLUMN_TYPES.NUMBER,
    variant: CURRENCY,
    cellDisplay: CurrencyDisplay(columns.amount),
    resizable: true
  },
  {
    headerName: locale['common.status'],
    field: columns.wirePaymentStatus,
    valueFormatter: ({ data }) => {
      if (data?.wirePaymentStatus === REQUIRES_OTHERS_APPROVAL)
        return locale['paymentRule.REQUIRES_OTHERS_APPROVAL'];
      return data?.wirePaymentStatus;
    },
    resizable: true,
    type: COLUMN_TYPES.MULTISELECT,
    filter: MultiSelectFilter,
    options: getMultiSelectOptions(records, columns.wirePaymentStatus)
  },
  {
    headerName: locale['common.payeeName'],
    field: columns.payeeName,
    resizable: true
  },
  {
    headerName: locale['common.fromAccountName'],
    field: columns.fromAccountName,
    resizable: true,
    valueFormatter: ({ data }) =>
      (data?.fromAccountNickName || data?.fromAccountName)?.trim() || locale['common.empty'],
    valueGetter: ({ data }) => (data?.fromAccountNickName || data?.fromAccountName)?.trim()
  },
  {
    headerName: locale['common.fromAccount'],
    field: columns.fromAccountNumber,
    resizable: true
  },
  {
    headerName: locale['common.transactionDate'],
    field: columns.date,
    valueFormatter: formatters.toDate({ locale, name: columns.date }),
    filter: DATE,
    type: COLUMN_TYPES.DATE,
    resizable: true
  },
  {
    field: columns.bankTrace,
    headerName: locale['common.bankTrace'],
    valueGetter: valueGetter(locale)
  },
  {
    field: columns.custTrace,
    headerName: locale['common.customerTrace'],
    valueGetter: valueGetter(locale)
  },
  {
    headerName: locale['common.paymentType'],
    field: columns.paymentType,
    type: COLUMN_TYPES.MULTISELECT,
    filter: MultiSelectFilter,
    resizable: true,
    options: getMultiSelectOptions(records, columns.paymentType)
  },
  {
    headerName: locale['common.paymentCategory'],
    field: columns.paymentCategory,
    type: COLUMN_TYPES.MULTISELECT,
    filter: MultiSelectFilter,
    resizable: true,
    options: getMultiSelectOptions(records, columns.paymentCategory)
  },
  {
    headerName: locale['sendPayments.approver'],
    field: columns.approver,
    resizable: true,
    sortable: true
  },
  {
    headerName: locale['sendPayments.approvedOn'],
    field: columns.approvedOn,
    type: COLUMN_TYPES.DATE,
    filter: DATE,
    resizable: true,
    sortable: true,
    valueFormatter: formatters.toDate({ locale, name: columns.approvedOn })
  },
  {
    headerName: locale['sendPayments.initiator'],
    field: columns.initiator,
    resizable: true,
    sortable: true
  },
  {
    headerName: locale['sendPayments.initiatedOn'],
    field: columns.initiatedOn,
    type: COLUMN_TYPES.DATE,
    filter: DATE,
    resizable: true,
    sortable: true,
    valueFormatter: formatters.toDate({ locale, name: columns.initiatedOn })
  },
  {
    headerName: locale['common.comments'],
    field: columns.comments,
    resizable: true
  },
  {
    field: columns.confirmationInfo,
    filterable: true,
    valueFormatter: ({ data }) => data?.confirmationInfo || locale['common.empty'],
    headerName: locale['common.confirmationInfo'],
    sortable: true,
    type: COLUMN_TYPES.STRING
  }
];

export default smbPaymentColumns;
